import React, { useEffect, useRef, useState } from "react";
import { useOpenPage, useWindowManager } from "./WindowManager";
import BootSequence from "./BootSequence";

const STARTUP_DELAY_MS = 900;

// Win98 dims the desktop behind the dialog with a 50% black dither.
const DITHER = "repeating-conic-gradient(#000 0 25%, transparent 0 50%) 0 0 / 2px 2px";

const DialogButton = ({ children, onClick, focused = false }) => (
  <button
    className={`w-[5.5rem] h-7 bg-[#c0c0c0] border-[0.13rem] border-t-white border-l-white border-b-black border-r-black active:border-t-black active:border-l-black active:border-b-white active:border-r-white ${
      focused ? "outline-dotted outline-1 outline-offset-[-5px]" : ""
    }`}
    onClick={onClick}
  >
    <span className="mt-[0.2rem] inline-block">{children}</span>
  </button>
);

const ShutDownDialog = ({ onConfirm, onCancel }) => {
  const [choice, setChoice] = useState("shutdown");

  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: DITHER }}>
      <div
        role="dialog"
        aria-label="Shut Down Aaron 98"
        className="w-[23rem] bg-[#c0c0c0] border-[0.12rem] border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-black border-r-black desktop-font text-xl select-none"
      >
        <div className="flex items-center justify-between h-[1.5rem] m-[0.15rem] pl-[0.3rem] pr-[0.15rem] bg-[#00007F] text-white">
          <span className="mt-[0.25rem]">Shut Down Aaron 98</span>
          <button
            className="w-4 h-4 flex items-center justify-center bg-[#c0c0c0] text-black text-sm leading-none border-[0.13rem] border-t-white border-l-white border-b-black border-r-black"
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>

        <div className="flex gap-4 px-4 pt-3 pb-4">
          <img
            src="/desktop-icons/my-computer-icon.png"
            alt=""
            className="w-12 h-12 shrink-0 [image-rendering:pixelated]"
            draggable={false}
          />
          <div className="flex flex-col gap-2">
            <p className="leading-[1.1]">What do you want the computer to do?</p>
            {[
              { value: "shutdown", label: "Shut down" },
              { value: "restart", label: "Restart" },
            ].map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 ml-2 cursor-default">
                <input
                  type="radio"
                  name="shutdown-choice"
                  value={value}
                  checked={choice === value}
                  onChange={() => setChoice(value)}
                />
                <span className="mt-[0.2rem]">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 pb-3">
          <DialogButton focused onClick={() => onConfirm(choice)}>
            OK
          </DialogButton>
          <DialogButton onClick={onCancel}>Cancel</DialogButton>
        </div>
      </div>
    </div>
  );
};

// The picture collapses to a line, then a dot, like a CRT monitor switching off.
const PowerOff = ({ onPowerOn }) => {
  useEffect(() => {
    window.addEventListener("keydown", onPowerOn);
    return () => window.removeEventListener("keydown", onPowerOn);
  }, [onPowerOn]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black cursor-pointer select-none"
      onClick={onPowerOn}
    >
      <div className="absolute inset-0 bg-[#e8eeff] animate-[crt-off_0.75s_ease-in_forwards] motion-reduce:hidden" />
      <p className="terminal-font text-[#666666] text-[1.6rem] opacity-0 animate-[fade-in_0.6s_ease-out_1.3s_forwards] motion-reduce:animate-none motion-reduce:opacity-100">
        Click to power on
      </p>
    </div>
  );
};

// stage: "dialog" | "off" | "restarting" | "starting". Powering back on from "off" runs the boot sequence.
const ShutDown = ({ stage, setStage }) => {
  const { reset } = useWindowManager();
  const openPageRef = useRef(null);
  openPageRef.current = useOpenPage();

  // After booting, the bare desktop sits for a beat before the portfolio opens, as startup apps did.
  useEffect(() => {
    if (stage !== "starting") return;
    const timer = setTimeout(() => {
      openPageRef.current("/");
      setStage(null);
    }, STARTUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [stage, setStage]);

  if (stage === "dialog") {
    return (
      <ShutDownDialog
        onConfirm={(choice) => {
          reset();
          setStage(choice === "restart" ? "restarting" : "off");
        }}
        onCancel={() => setStage(null)}
      />
    );
  }

  if (stage === "off") {
    return <PowerOff onPowerOn={() => setStage("restarting")} />;
  }

  if (stage === "restarting") {
    return <BootSequence onDone={() => setStage("starting")} />;
  }

  return null;
};

export default ShutDown;
